import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  LayoutDashboard,
  FolderKanban,
  DollarSign,
  BarChart3,
  Settings,
  FileText,
  Search
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface CommandItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  keywords: string[];
  action: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: (page: string) => void;
}

const navigationCommands: Omit<CommandItem, 'action'>[] = [
  { id: 'dashboard', label: 'Homepage', icon: LayoutDashboard, keywords: ['home', 'overview', 'main', 'dashboard'] },
  { id: 'projects', label: 'Projects', icon: FolderKanban, keywords: ['project', 'work'] },
  // { id: 'portfolio', label: 'Portfolio', icon: Briefcase, keywords: ['portfolio', 'overview'] }, // Hidden for Release 1
  // { id: 'utilisation', label: 'Utilisation', icon: TrendingUp, keywords: ['utilisation', 'usage', 'capacity'] }, // Hidden for Release 1
  { id: 'wip-reporting', label: 'WIP', icon: FileText, keywords: ['wip', 'work in progress', 'reporting'] },
  { id: 'profitability', label: 'Profitability', icon: DollarSign, keywords: ['profit', 'revenue', 'financial'] },
  { id: 'budgets', label: 'Budgets & Variance', icon: BarChart3, keywords: ['budget', 'variance', 'forecast'] },
  { id: 'project-transactions', label: 'Project Transactions', icon: FileText, keywords: ['transactions', 'reports', 'project'] },
  // { id: 'gantt', label: 'Gantt Charts', icon: Calendar, keywords: ['gantt', 'timeline', 'schedule'] }, // Hidden for Release 1
  { id: 'settings', label: 'Settings', icon: Settings, keywords: ['settings', 'preferences', 'config', 'labs', 'experimental', 'features'] },
];

export function CommandPalette({ open, onOpenChange, onNavigate }: CommandPaletteProps) {
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const commands: CommandItem[] = navigationCommands.map(cmd => ({
    ...cmd,
    action: () => {
      onNavigate(cmd.id);
      onOpenChange(false);
      setSearch('');
    }
  }));

  const filteredCommands = commands.filter(cmd => {
    const searchLower = search.toLowerCase();
    return (
      cmd.label.toLowerCase().includes(searchLower) ||
      cmd.keywords.some(keyword => keyword.toLowerCase().includes(searchLower))
    );
  });

  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelectedIndex(0);
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % filteredCommands.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + filteredCommands.length) % filteredCommands.length);
    } else if (e.key === 'Enter' && filteredCommands[selectedIndex]) {
      e.preventDefault();
      filteredCommands[selectedIndex].action();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 max-w-2xl" onKeyDown={handleKeyDown}>
        <div className="flex items-center border-b px-4 py-3">
          <Search className="h-4 w-4 text-muted-foreground mr-2" />
          <Input
            placeholder="Search pages and commands..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0"
            autoFocus
          />
        </div>

        <div className="max-h-[400px] overflow-y-auto p-2">
          {filteredCommands.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No results found
            </div>
          ) : (
            <div className="space-y-1">
              {filteredCommands.map((cmd, idx) => {
                const Icon = cmd.icon;
                return (
                  <button
                    key={cmd.id}
                    onClick={() => cmd.action()}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors',
                      'hover:bg-accent',
                      selectedIndex === idx && 'bg-accent'
                    )}
                  >
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span>{cmd.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t px-4 py-2 text-xs text-muted-foreground flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 text-[10px] font-semibold bg-muted border border-border rounded">↑</kbd>
              <kbd className="px-1.5 py-0.5 text-[10px] font-semibold bg-muted border border-border rounded">↓</kbd>
              to navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 text-[10px] font-semibold bg-muted border border-border rounded">↵</kbd>
              to select
            </span>
          </div>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 text-[10px] font-semibold bg-muted border border-border rounded">Esc</kbd>
            to close
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
